import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import {
  Activity,
  Building2,
  Check,
  CircleAlert,
  Clock3,
  Database,
  FileCheck2,
  FileKey2,
  Hash,
  LoaderCircle,
  LockKeyhole,
  LogOut,
  Network,
  Plus,
  RefreshCw,
  ShieldCheck,
  UploadCloud,
  UserRoundCheck,
  X,
} from "lucide-react";
import { ApiFailure, api } from "./api.js";
import { isMysqlMe, type LegacyMe, type Me, type MysqlMe } from "./auth-types.js";
import { AuthPortal } from "./AuthPortal.js";
import { BrandMark } from "./BrandMark.js";
import { IdentityOnboarding } from "./IdentityOnboarding.js";
import { ReviewWorkbench } from "./ReviewWorkbench.js";
import { PlatformSecurityWorkbench } from "./PlatformSecurityWorkbench.js";

interface Organization {
  id: string;
  legalName: string;
  assuranceLevel: string;
  status: string;
}

interface Evidence {
  id: string;
  mode: "L1" | "L2";
  title: string;
  businessRef: string | null;
  digestAlgorithm: string | null;
  digest: string | null;
  state: "DRAFT" | "RECEIVED";
  processingStatus: "METADATA_ONLY" | "UPLOAD_IN_PROGRESS" | "SCAN_PENDING" | "HASH_MISMATCH" | "REJECTED" | "MANIFESTED";
  custodyMode: "CUSTOMER_SELF" | "LIANMAI_TRUSTEE_LOCAL" | null;
  chainStatus: "NOT_SUBMITTED" | "SUBMITTED" | "CONFIRMED" | "FAILED";
  chainSubmitted: boolean;
  chainMessage: string;
  chainTask: null | {
    id: string;
    state: "READY" | "LEASED" | "RETRY_WAIT" | "BLOCKED_ADAPTER_NOT_CONFIGURED" | "SUBMITTED" | "DEAD_LETTER";
    message: string;
    requestId: string;
    attemptCount: 0;
  };
  manifest: null | { id: string; schemaId: string; manifestHash: string; createdAt: string };
  createdAt: string;
  requestId: string;
}

interface AuditEvent {
  id: string;
  action: string;
  objectType: string;
  objectId: string | null;
  requestId: string;
  outcome: string;
  occurredAt: string;
}

interface UploadSession {
  id: string;
  evidenceId: string;
  state: string;
  expectedDigest: string;
  expectedSize: number;
  partSize: number;
  totalParts: number;
  uploadedSize: number;
  uploadedParts: Array<{ partNumber: number; byteSize: number; digest: string }>;
  expiresAt: string;
}

interface ActiveUpload {
  id: string;
  token: string;
  evidenceId: string;
  digest: string;
  size: number;
  fileName: string;
  partSize: number;
  totalParts: number;
}

const ACTIVE_UPLOAD_SESSION_KEY = "lmx.active-l2-upload.v1";

function activeUploadStorageKey(tenantId: string): string {
  return `${ACTIVE_UPLOAD_SESSION_KEY}.${tenantId}`;
}

function storedActiveUpload(tenantId: string): ActiveUpload | null {
  try {
    const value = sessionStorage.getItem(activeUploadStorageKey(tenantId));
    if (!value) return null;
    const parsed = JSON.parse(value) as Partial<ActiveUpload>;
    if (typeof parsed.id !== "string" || typeof parsed.token !== "string" || typeof parsed.evidenceId !== "string" ||
        typeof parsed.digest !== "string" || typeof parsed.size !== "number" || typeof parsed.fileName !== "string" ||
        typeof parsed.partSize !== "number" || typeof parsed.totalParts !== "number") return null;
    return parsed as ActiveUpload;
  } catch {
    return null;
  }
}

function shortId(value: string, head = 8): string {
  return value.length > head * 2 + 1 ? `${value.slice(0, head)}…${value.slice(-head)}` : value;
}

function formatTime(value: string): string {
  return new Intl.DateTimeFormat("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function roleName(role: string): string {
  const names: Record<string, string> = {
    PLATFORM_ADMIN: "平台管理员",
    ORG_ADMIN: "企业管理员",
    EVIDENCE_CREATOR: "存证经办人",
    EVIDENCE_VIEWER: "存证查看人",
    AUDITOR: "审计员",
  };
  return names[role] ?? role;
}

function declaredMimeForFile(file: File): string {
  if (file.type) return file.type.toLowerCase();
  const extension = file.name.toLowerCase().split(".").at(-1);
  const byExtension: Record<string, string> = {
    pdf: "application/pdf",
    png: "image/png",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    txt: "text/plain",
    csv: "text/plain",
    log: "text/plain",
    json: "application/json",
  };
  return byExtension[extension ?? ""] ?? "application/octet-stream";
}

function ErrorNotice({ error }: { error: ApiFailure }): React.JSX.Element {
  return (
    <div className="error-notice" role="alert">
      <CircleAlert size={18} />
      <span><b>{error.message}</b><small>{error.code}{error.requestId ? ` · 请求 ${shortId(error.requestId, 6)}` : ""}</small></span>
    </div>
  );
}

function StatusBadge({ evidence }: { evidence: Evidence }): React.JSX.Element {
  if (evidence.processingStatus === "MANIFESTED") return <span className="status status-received"><ShieldCheck />Manifest 已形成</span>;
  if (evidence.processingStatus === "UPLOAD_IN_PROGRESS") return <span className="status status-draft"><UploadCloud />上传中</span>;
  if (["HASH_MISMATCH", "REJECTED", "SCAN_PENDING"].includes(evidence.processingStatus)) return <span className="status status-warning"><CircleAlert />需处理</span>;
  if (evidence.state === "DRAFT") return <span className="status status-draft"><Clock3 />草稿</span>;
  return <span className="status status-received"><Check />已接收</span>;
}

function Workbench({
  initialMe,
  onLogout,
  onOpenIdentity,
  onOpenReview,
  onOpenSecurity,
}: {
  initialMe: LegacyMe | MysqlMe;
  onLogout: () => void;
  onOpenIdentity?: () => void;
  onOpenReview?: () => void;
  onOpenSecurity?: () => void;
}): React.JSX.Element {
  const isMysql = isMysqlMe(initialMe);
  const mysqlContext = isMysql ? initialMe.trustedContext : null;
  if (isMysql && !mysqlContext) throw new Error("MYSQL_TRUSTED_CONTEXT_REQUIRED");
  const me = isMysql ? {
    identityStore: "MYSQL" as const,
    displayName: mysqlContext!.displayName,
    roles: mysqlContext!.roles,
    tenantId: mysqlContext!.tenantId,
    subjectId: mysqlContext!.subjectId,
    expiresAt: initialMe.session.expiresAt,
    csrfToken: null,
    capabilities: mysqlContext!.capabilities,
    featureFlags: mysqlContext!.featureFlags,
    scopeType: mysqlContext!.scopeType,
  } : {
    ...initialMe,
    csrfToken: initialMe.csrfToken,
    scopeType: "ORGANIZATION" as const,
  };
  const apiPrefix = isMysql ? "/v2" : "/v1";
  const mutationHeaders = (headers: Record<string, string> = {}): Record<string, string> => ({
    ...(me.csrfToken ? { "x-csrf-token": me.csrfToken } : {}),
    ...headers,
  });
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [evidence, setEvidence] = useState<Evidence[]>([]);
  const [audit, setAudit] = useState<AuditEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [formOpen, setFormOpen] = useState(true);
  const [mode, setMode] = useState<"L1" | "L2">("L1");
  const [title, setTitle] = useState("");
  const [businessRef, setBusinessRef] = useState("");
  const [digest, setDigest] = useState("");
  const [fileName, setFileName] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [hashing, setHashing] = useState(false);
  const [hashProgress, setHashProgress] = useState(0);
  const [saving, setSaving] = useState<"draft" | "received" | "l2" | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStage, setUploadStage] = useState("");
  const [activeUpload, setActiveUpload] = useState<ActiveUpload | null>(() => storedActiveUpload(me.tenantId));
  const [error, setError] = useState<ApiFailure | null>(null);
  const [success, setSuccess] = useState<{ id: string; requestId?: string; state: string } | null>(null);
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const hashWorkerRef = useRef<Worker | null>(null);
  const hashJobRef = useRef<string | null>(null);

  const loadData = useCallback(async (quiet = false): Promise<void> => {
    quiet ? setRefreshing(true) : setLoading(true);
    setError(null);
    try {
      const organizationRequest = isMysql
        ? Promise.resolve({ data: {
            id: mysqlContext!.organizationId ?? mysqlContext!.personalIdentityId ?? mysqlContext!.subjectId,
            legalName: mysqlContext!.displayName,
            assuranceLevel: mysqlContext!.assuranceLevel,
            status: "VERIFIED",
          } satisfies Organization })
        : api<Organization>("/v1/organizations/current");
      const requests: [Promise<{ data: Organization }>, Promise<{ data: Evidence[] }>, Promise<{ data: AuditEvent[] }> | null] = [
        organizationRequest,
        api<Evidence[]>(`${apiPrefix}/evidence-records`),
        me.capabilities.viewAudit ? api<AuditEvent[]>(`${apiPrefix}/audit-events?limit=8`) : null,
      ];
      const [organizationResponse, evidenceResponse, auditResponse] = await Promise.all([
        requests[0],
        requests[1],
        requests[2] ?? Promise.resolve({ data: [] as AuditEvent[] }),
      ]);
      setOrganization(organizationResponse.data);
      setEvidence(evidenceResponse.data);
      setAudit(auditResponse.data);
    } catch (cause) {
      setError(cause instanceof ApiFailure ? cause : new ApiFailure(500, "UNKNOWN", "无法加载工作台数据。"));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [apiPrefix, isMysql, me.capabilities.viewAudit, mysqlContext]);

  useEffect(() => { void loadData(); }, [loadData]);

  useEffect(() => () => hashWorkerRef.current?.terminate(), []);

  useEffect(() => {
    if (activeUpload) {
      sessionStorage.setItem(activeUploadStorageKey(me.tenantId), JSON.stringify(activeUpload));
      setMode("L2");
    } else {
      sessionStorage.removeItem(activeUploadStorageKey(me.tenantId));
    }
  }, [activeUpload, me.tenantId]);

  const stats = useMemo(() => ({
    total: evidence.length,
    drafts: evidence.filter((item) => item.state === "DRAFT").length,
    received: evidence.filter((item) => item.state === "RECEIVED").length,
  }), [evidence]);

  const chooseFile = async (file: File | undefined): Promise<void> => {
    if (!file) return;
    hashWorkerRef.current?.terminate();
    const worker = new Worker(new URL("./hash.worker.ts", import.meta.url), { type: "module" });
    const jobId = crypto.randomUUID();
    hashWorkerRef.current = worker;
    hashJobRef.current = jobId;
    setSelectedFile(file);
    setHashing(true);
    setHashProgress(0);
    setError(null);
    try {
      const fileDigest = await new Promise<string>((resolve, reject) => {
        worker.onmessage = (event: MessageEvent<Record<string, unknown>>) => {
          if (event.data.jobId !== jobId) return;
          if (event.data.type === "progress") {
            const processed = Number(event.data.processed);
            const total = Number(event.data.total);
            setHashProgress(total > 0 ? Math.round(processed / total * 100) : 0);
          } else if (event.data.type === "complete" && typeof event.data.digest === "string") {
            resolve(event.data.digest);
          } else if (event.data.type === "cancelled") {
            reject(new Error("HASH_CANCELLED"));
          } else if (event.data.type === "error") {
            reject(new Error(typeof event.data.message === "string" ? event.data.message : "HASH_WORKER_FAILED"));
          }
        };
        worker.onerror = () => reject(new Error("HASH_WORKER_FAILED"));
        worker.postMessage({ type: "hash", jobId, file, chunkSize: 4 * 1024 * 1024 });
      });
      setDigest(fileDigest);
      setFileName(`${file.name} · ${(file.size / 1024).toFixed(1)} KB`);
      if (activeUpload && (activeUpload.digest !== fileDigest || activeUpload.size !== file.size || activeUpload.fileName !== file.name)) {
        setError(new ApiFailure(409, "RESUME_FILE_MISMATCH", "当前文件与待续传会话不一致；请重新选择原文件，或先取消旧会话。"));
      }
    } catch (cause) {
      if (!(cause instanceof Error && cause.message === "HASH_CANCELLED")) {
        setError(new ApiFailure(400, "FILE_DIGEST_FAILED", "浏览器分块摘要计算失败。"));
      }
      setSelectedFile(null);
    } finally {
      worker.terminate();
      if (hashWorkerRef.current === worker) hashWorkerRef.current = null;
      if (hashJobRef.current === jobId) hashJobRef.current = null;
      setHashing(false);
    }
  };

  const cancelHash = (): void => {
    if (hashWorkerRef.current && hashJobRef.current) {
      hashWorkerRef.current.postMessage({ type: "cancel", jobId: hashJobRef.current });
    }
  };

  const save = async (saveAsDraft: boolean): Promise<void> => {
    setSaving(saveAsDraft ? "draft" : "received");
    setError(null);
    setSuccess(null);
    try {
      const response = await api<Evidence>(`${apiPrefix}/evidence-records`, {
        method: "POST",
        headers: mutationHeaders({ "idempotency-key": idempotencyKey }),
        body: JSON.stringify({
          mode,
          title,
          businessRef,
          digestAlgorithm: digest ? "SHA-256" : undefined,
          digest: digest || undefined,
          saveAsDraft,
        }),
      });
      setSuccess({ id: response.data.id, state: response.data.state, requestId: response.requestId });
      setTitle("");
      setBusinessRef("");
      setDigest("");
      setFileName("");
      setSelectedFile(null);
      setHashProgress(0);
      setIdempotencyKey(crypto.randomUUID());
      await loadData(true);
    } catch (cause) {
      setError(cause instanceof ApiFailure ? cause : new ApiFailure(500, "UNKNOWN", "保存失败。"));
    } finally {
      setSaving(null);
    }
  };

  const submitL2 = async (): Promise<void> => {
    if (!selectedFile || !digest) {
      setError(new ApiFailure(400, "FILE_REQUIRED", "请先选择文件并完成浏览器分块摘要。"));
      return;
    }
    if (!activeUpload && !title.trim()) {
      setError(new ApiFailure(400, "TITLE_REQUIRED", "请填写存证标题。"));
      return;
    }
    if (activeUpload && (activeUpload.digest !== digest || activeUpload.size !== selectedFile.size || activeUpload.fileName !== selectedFile.name)) {
      setError(new ApiFailure(409, "RESUME_FILE_MISMATCH", "所选文件与待续传会话不一致。"));
      return;
    }
    setSaving("l2");
    setError(null);
    setSuccess(null);
    setUploadProgress(0);
    try {
      let currentUpload = activeUpload;
      let uploadStatus: UploadSession;
      if (!currentUpload) {
        setUploadStage("正在创建 L2 存证草稿");
        const draft = await api<Evidence>(`${apiPrefix}/evidence-records`, {
          method: "POST",
          headers: mutationHeaders({ "idempotency-key": idempotencyKey }),
          body: JSON.stringify({
            mode: "L2",
            title,
            businessRef,
            digestAlgorithm: "SHA-256",
            digest,
            saveAsDraft: true,
          }),
        });
        setUploadStage("正在初始化短效分片会话");
        const initialized = await api<UploadSession>(`${apiPrefix}/evidence-records/${draft.data.id}/uploads`, {
          method: "POST",
          headers: mutationHeaders(),
          body: JSON.stringify({
            fileName: selectedFile.name,
            declaredMime: declaredMimeForFile(selectedFile),
            expectedDigest: digest,
            expectedSize: selectedFile.size,
          }),
        });
        if (!initialized.uploadCredential?.token) throw new ApiFailure(500, "UPLOAD_CREDENTIAL_MISSING", "服务端未返回上传凭证。", initialized.requestId);
        currentUpload = {
          id: initialized.data.id,
          token: initialized.uploadCredential.token,
          evidenceId: draft.data.id,
          digest,
          size: selectedFile.size,
          fileName: selectedFile.name,
          partSize: initialized.data.partSize,
          totalParts: initialized.data.totalParts,
        };
        setActiveUpload(currentUpload);
        uploadStatus = initialized.data;
      } else {
        setUploadStage("正在读取断点状态");
        uploadStatus = (await api<UploadSession>(`${apiPrefix}/uploads/${currentUpload.id}`)).data;
        if (uploadStatus.state === "COMPLETED") {
          setSuccess({ id: currentUpload.evidenceId, state: "MANIFESTED" });
          setActiveUpload(null);
          setUploadProgress(100);
          setUploadStage("服务端已完成 L2 Manifest");
          await loadData(true);
          return;
        }
      }

      const uploadedParts = new Set(uploadStatus.uploadedParts.map((part) => part.partNumber));
      let uploadedBytes = uploadStatus.uploadedSize;
      setUploadProgress(Math.round(uploadedBytes / selectedFile.size * 100));
      for (let partNumber = 1; partNumber <= currentUpload.totalParts; partNumber += 1) {
        if (uploadedParts.has(partNumber)) continue;
        const start = (partNumber - 1) * currentUpload.partSize;
        const end = Math.min(start + currentUpload.partSize, selectedFile.size);
        const chunk = selectedFile.slice(start, end);
        setUploadStage(`正在上传分片 ${partNumber}/${currentUpload.totalParts}`);
        await api<Record<string, unknown>>(`${apiPrefix}/uploads/${currentUpload.id}/parts/${partNumber}`, {
          method: "PUT",
          headers: mutationHeaders({
            "content-type": "application/octet-stream",
            "x-upload-token": currentUpload.token,
          }),
          body: chunk,
        });
        uploadedBytes += chunk.size;
        setUploadProgress(Math.round(uploadedBytes / selectedFile.size * 100));
      }
      setUploadStage("服务端正在复算摘要、扫描并信封加密");
      const completed = await api<{
        evidenceId: string;
        manifest: { manifestHash: string };
        chainTask: { state: string };
      }>(`${apiPrefix}/uploads/${currentUpload.id}/complete`, {
        method: "POST",
        headers: mutationHeaders({ "x-upload-token": currentUpload.token }),
      });
      setSuccess({ id: completed.data.evidenceId, state: "MANIFESTED", requestId: completed.requestId });
      setActiveUpload(null);
      setTitle("");
      setBusinessRef("");
      setDigest("");
      setFileName("");
      setSelectedFile(null);
      setHashProgress(0);
      setUploadProgress(100);
      setUploadStage("L2 Manifest 已形成；链任务未执行");
      setIdempotencyKey(crypto.randomUUID());
      await loadData(true);
    } catch (cause) {
      const failure = cause instanceof ApiFailure ? cause : new ApiFailure(500, "UNKNOWN", "L2 上传流程失败，可重新选择同一文件续传。" );
      setError(failure);
      if (failure.status === 422 || failure.code === "HASH_MISMATCH") {
        setActiveUpload(null);
      }
    } finally {
      setSaving(null);
    }
  };

  const cancelActiveUpload = async (): Promise<void> => {
    if (!activeUpload) return;
    setSaving("l2");
    setError(null);
    try {
      await api<UploadSession>(`${apiPrefix}/uploads/${activeUpload.id}`, {
        method: "DELETE",
        headers: mutationHeaders({ "x-upload-token": activeUpload.token }),
      });
      setActiveUpload(null);
      setUploadProgress(0);
      setUploadStage("上传会话已取消，隔离分片已清理");
    } catch (cause) {
      setError(cause instanceof ApiFailure ? cause : new ApiFailure(500, "UNKNOWN", "无法取消上传会话。"));
    } finally {
      setSaving(null);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await api<void>(`${apiPrefix}/auth/session`, { method: "DELETE", headers: mutationHeaders() });
    } finally {
      onLogout();
    }
  };

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand brand-on-dark"><BrandMark /><span><b>链脉信枢</b><small>LIANMAI TRUST HUB</small></span></div>
        <nav aria-label="主导航">
          <a className="active" href="#overview"><Activity />总览</a>
          <a href="#new-evidence"><FileCheck2 />可信存证</a>
          <a href="#audit"><ShieldCheck />审计记录</a>
          {isMysql && onOpenIdentity && <a href="#identity-center" onClick={(event) => { event.preventDefault(); onOpenIdentity(); }}><UserRoundCheck />身份中心</a>}
          {isMysql && initialMe.capabilities.reviewIdentities && onOpenReview && <a href="#review" onClick={(event) => { event.preventDefault(); onOpenReview(); }}><ShieldCheck />审核工作台</a>}
          {isMysql && initialMe.capabilities.viewPlatformSecurity && onOpenSecurity && <a href="#platform-security" onClick={(event) => { event.preventDefault(); onOpenSecurity(); }}><LockKeyhole />平台安全</a>}
          <span className="nav-divider">即将接通</span>
          <a className="disabled"><Network />联盟链任务<small>未启用</small></a>
          <a className="disabled"><Hash />公共验证<small>未启用</small></a>
        </nav>
        <div className="sidebar-foot">
          <span className="environment"><i />本地真实数据环境</span>
          <small>链适配器未接入业务流</small>
        </div>
      </aside>

      <main className="workspace">
        <header className="topbar">
          <div><span className="breadcrumb">{me.scopeType === "PERSONAL" ? "个人可信空间" : "企业控制台"} /</span><b>可信存证工作台</b></div>
          <div className="user-area">
            <span className="user-avatar">{me.displayName.slice(0, 1)}</span>
            <span><b>{me.displayName}</b><small>{roleName(me.roles[0] ?? "")}</small></span>
            <button aria-label="退出登录" className="icon-button" onClick={() => void logout()}><LogOut /></button>
          </div>
        </header>

        <div className="content">
          <section className="welcome" id="overview">
            <div>
              <span className="eyebrow dark"><ShieldCheck size={15} /> 已通过双因素认证</span>
              <h1>晚上好，{me.displayName}</h1>
              <p>当前主体可完成 L1 摘要接收，也可把 L2 原文件分片送入隔离区，经服务端复算、策略扫描和信封加密后形成 Manifest；联盟链仍未启用。</p>
            </div>
            <button className="secondary-button" disabled={refreshing} onClick={() => void loadData(true)}>{refreshing ? <LoaderCircle className="spin" /> : <RefreshCw />}刷新状态</button>
          </section>

          <section className="boundary-banner">
            <span className="boundary-icon"><Database /></span>
            <div><b>S2 运行边界：L2 本地受托闭环已连接，联盟链仍未连接</b><p>原文件只以隔离分片进入服务端，摘要匹配和本地策略扫描通过后写入 AES-256-GCM 密文；本地主密钥代表链脉受托控制，不是客户自持或双控，且不会生成 txId 或区块高度。</p></div>
            <span className="truth-label">无假成功</span>
          </section>

          <section className="stats-grid">
            <article><span><FileCheck2 /></span><div><small>存证对象</small><strong>{loading ? "—" : stats.total}</strong><em>当前租户</em></div></article>
            <article><span className="violet"><Clock3 /></span><div><small>草稿</small><strong>{loading ? "—" : stats.drafts}</strong><em>可继续完善</em></div></article>
            <article><span className="green"><Check /></span><div><small>已接收</small><strong>{loading ? "—" : stats.received}</strong><em>L1 或 L2 Manifest</em></div></article>
            <article><span className="amber"><Network /></span><div><small>链上确认</small><strong>0</strong><em>功能未启用</em></div></article>
          </section>

          <section className="identity-card">
            <div className="section-heading"><div><span className="heading-icon"><Building2 /></span><span><b>{organization?.legalName ?? "正在读取企业…"}</b><small>当前可信身份上下文</small></span></div><span className="verified"><UserRoundCheck />内部审核身份</span></div>
            <div className="identity-details">
              <span><small>租户隔离标识</small><code title={me.tenantId}>{shortId(me.tenantId)}</code></span>
              <span><small>主体标识</small><code title={me.subjectId}>{shortId(me.subjectId)}</code></span>
              <span><small>有效角色</small><b>{me.roles.map(roleName).join("、")}</b></span>
              <span><small>会话到期</small><b>{formatTime(me.expiresAt)}</b></span>
            </div>
          </section>

          <section className="panel create-panel" id="new-evidence">
            <div className="panel-header">
              <div><span className="heading-icon accent"><Plus /></span><span><h2>创建存证</h2><p>先形成可追踪对象，再由后续异步任务提交联盟链。</p></span></div>
              <button className="text-button compact" onClick={() => setFormOpen((open) => !open)}>{formOpen ? "收起" : "展开"}</button>
            </div>
            {formOpen && (
              <div className="evidence-form">
                <div className="mode-picker" role="radiogroup" aria-label="存证模式">
                  <button disabled={Boolean(activeUpload)} className={mode === "L1" ? "selected" : ""} onClick={() => setMode("L1")}><span><Hash /></span><b>L1 摘要存证</b><small>文件只在浏览器读取，不上传</small>{mode === "L1" && <Check />}</button>
                  <button disabled={Boolean(activeUpload)} className={mode === "L2" ? "selected" : ""} onClick={() => setMode("L2")}><span><FileKey2 /></span><b>L2 文件认证</b><small>分片上传 · 服务端复算 · 加密存储</small>{mode === "L2" && <Check />}</button>
                </div>
                <div className="form-grid">
                  <label>存证标题 <b>*</b><input value={title} onChange={(event) => setTitle(event.target.value)} maxLength={120} placeholder="例如：2026 年采购合同原件" /></label>
                  <label>业务关联号 <small>选填</small><input value={businessRef} onChange={(event) => setBusinessRef(event.target.value)} maxLength={120} placeholder="例如：HT-2026-0902-001" /></label>
                </div>
                <div className="digest-zone">
                  <div>
                    <span className="digest-icon"><FileKey2 /></span>
                    <span><b>{mode === "L1" ? "选择本地文件计算 SHA-256" : "选择原文件并计算分块摘要"}</b><small>{mode === "L1" ? "Web Worker 按 4 MiB 分块读取；原文件不离开浏览器。" : "先由 Web Worker 分块计算摘要，再按服务端分片协议上传；支持进度、取消和同文件续传。"}</small></span>
                  </div>
                  <div className="file-actions">
                    <label className="file-button">{hashing ? <LoaderCircle className="spin" /> : <Hash />}{hashing ? `摘要 ${hashProgress}%` : "选择文件"}<input type="file" accept=".pdf,.png,.jpg,.jpeg,.txt,.csv,.log,.json" disabled={hashing || Boolean(saving)} onChange={(event) => void chooseFile(event.target.files?.[0])} /></label>
                    {hashing && <button className="icon-button cancel-button" aria-label="取消摘要计算" onClick={cancelHash}><X /></button>}
                  </div>
                </div>
                {hashing && <div className="progress-track" aria-label={`摘要进度 ${hashProgress}%`}><i style={{ width: `${hashProgress}%` }} /></div>}
                {(digest || fileName) && <div className="digest-result"><Check /><span><b>{fileName}</b><code title={digest}>{digest}</code></span></div>}
                <div className="idempotency-row"><span><LockKeyhole />本次幂等键</span><code title={idempotencyKey}>{shortId(idempotencyKey)}</code><small>重复请求只返回同一个 evidenceId</small></div>
                {mode === "L2" && <div className="warning-notice"><CircleAlert /><span><b>L2 当前为本地链脉受托模式</b><small>本地主密钥控制租户 KEK，因此平台技术上具备受控解密基础，但当前没有下载/解密接口、审批和 HSM。LOCAL_POLICY_ONLY 也不是生产级恶意软件扫描；生产环境默认拒绝启用。</small></span></div>}
                {activeUpload && <div className="resume-notice"><UploadCloud /><span><b>检测到待续传会话</b><small>uploadId：{shortId(activeUpload.id)}。重新选择原文件，摘要匹配后会跳过已完成分片；凭证仅保存在当前标签页会话中。</small></span></div>}
                {saving === "l2" && <div className="upload-progress"><span><b>{uploadStage}</b><em>{uploadProgress}%</em></span><div className="progress-track"><i style={{ width: `${uploadProgress}%` }} /></div></div>}
                {error && <ErrorNotice error={error} />}
                {success && <div className="success-notice"><Check /><span><b>{success.state === "DRAFT" ? "存证草稿已真实保存" : success.state === "MANIFESTED" ? "L2 密文与 Manifest 已真实形成" : "L1 摘要已真实接收"}</b><small>evidenceId：{success.id} · 请求：{success.requestId ? shortId(success.requestId) : "—"} · 尚未上链</small></span></div>}
                <div className="form-actions">
                  <button className="secondary-button" disabled={!title.trim() || Boolean(saving) || Boolean(activeUpload)} onClick={() => void save(true)}>{saving === "draft" ? <LoaderCircle className="spin" /> : <Database />}保存真实草稿</button>
                  <button className="primary-button inline" disabled={hashing || !digest || !selectedFile || Boolean(saving) || (!activeUpload && !title.trim())} onClick={() => void (mode === "L1" ? save(false) : submitL2())}>{saving === "received" || saving === "l2" ? <LoaderCircle className="spin" /> : mode === "L1" ? <FileCheck2 /> : <UploadCloud />}{mode === "L1" ? "接收 L1 摘要" : activeUpload ? "继续 L2 上传" : "上传并形成 Manifest"}</button>
                  {activeUpload && <button className="text-button danger-text" disabled={Boolean(saving)} onClick={() => void cancelActiveUpload()}><X />取消上传并清理分片</button>}
                </div>
              </div>
            )}
          </section>

          <section className="panel records-panel">
            <div className="panel-header"><div><span className="heading-icon"><Database /></span><span><h2>最近存证</h2><p>来自当前租户的真实数据库记录</p></span></div><span className="record-count">{evidence.length} 条</span></div>
            {loading ? <div className="empty"><LoaderCircle className="spin" /><p>正在读取数据库…</p></div> : evidence.length === 0 ? (
              <div className="empty"><FileCheck2 /><h3>还没有存证对象</h3><p>填写上方表单，创建第一个真实草稿。</p></div>
            ) : (
              <div className="record-list">
                {evidence.map((item) => (
                  <article key={item.id}>
                    <span className="record-icon"><FileKey2 /></span>
                    <div className="record-main"><span><b>{item.title}</b><StatusBadge evidence={item} /><span className="status status-chain"><Network />{item.chainMessage}</span></span><small>{item.mode} · {item.custodyMode === "LIANMAI_TRUSTEE_LOCAL" ? "本地链脉受托" : item.custodyMode === "CUSTOMER_SELF" ? "客户自持原文件" : "托管模式未选择"} · {item.businessRef || "无业务关联号"} · {formatTime(item.createdAt)}</small><code title={item.id}>evidenceId {shortId(item.id)}{item.manifest ? ` · manifest ${shortId(item.manifest.manifestHash, 6)}` : ""}</code></div>
                    <div className="record-side"><small>请求关联号</small><code title={item.requestId}>{shortId(item.requestId, 6)}</code>{item.digest && <span title={item.digest}><Hash />{shortId(item.digest, 6)}</span>}</div>
                  </article>
                ))}
              </div>
            )}
          </section>

          {me.capabilities.viewAudit && (
            <section className="panel audit-panel" id="audit">
              <div className="panel-header"><div><span className="heading-icon"><ShieldCheck /></span><span><h2>审计事件</h2><p>按 requestId 关联身份与存证动作；事件链禁止更新和删除</p></span></div><span className="verified"><ShieldCheck />哈希链</span></div>
              <div className="audit-list">
                {audit.map((event) => <div key={event.id}><i /><span><b>{event.action}</b><small>{event.objectType}{event.objectId ? ` · ${shortId(event.objectId, 6)}` : ""}</small></span><code title={event.requestId}>{shortId(event.requestId, 6)}</code><time>{formatTime(event.occurredAt)}</time></div>)}
              </div>
            </section>
          )}
          <footer className="workspace-footer"><span>链脉信枢 · {isMysql ? "MySQL 可信身份与存证工作台 v0.7 D10" : "兼容存证工作台 v0.5.0"}</span><span>lianmai.net.cn · 京ICP备2026024994号-1</span></footer>
        </div>
      </main>
    </div>
  );
}

export function App(): React.JSX.Element {
  const [phase, setPhase] = useState<"checking" | "anonymous" | "authenticated">("checking");
  const [me, setMe] = useState<Me | null>(null);
  const [mysqlView, setMysqlView] = useState<"identity" | "review" | "evidence" | "security">("identity");

  const acceptAuthenticated = (current: Me): void => {
    setMe(current);
    setPhase("authenticated");
    if (isMysqlMe(current)) {
      setMysqlView(current.capabilities.viewPlatformSecurity ? "security" : current.trustedContext ? "evidence" : current.capabilities.reviewIdentities ? "review" : "identity");
    }
  };

  useEffect(() => {
    let active = true;
    void api<Me>("/v2/me").then((response) => {
      if (active) acceptAuthenticated(response.data);
    }).catch(() => {
      if (active) setPhase("anonymous");
    });
    return () => { active = false; };
  }, []);

  if (phase === "checking") return <div className="boot-screen"><BrandMark /><LoaderCircle className="spin" /><span>正在验证本机会话…</span></div>;
  if (phase === "anonymous" || !me) return <AuthPortal onAuthenticated={acceptAuthenticated} />;
  if (isMysqlMe(me)) {
    if (mysqlView === "review" && me.capabilities.reviewIdentities) {
      return <ReviewWorkbench
        me={me}
        onBack={() => setMysqlView("identity")}
        onOpenEvidence={() => setMysqlView("evidence")}
        onOpenSecurity={() => setMysqlView("security")}
        onLogout={() => { setMe(null); setPhase("anonymous"); setMysqlView("identity"); }}
      />;
    }
    if (mysqlView === "security" && me.capabilities.viewPlatformSecurity) {
      return <PlatformSecurityWorkbench
        me={me}
        onBack={() => setMysqlView("identity")}
        onOpenEvidence={() => setMysqlView("evidence")}
        onOpenReview={() => setMysqlView("review")}
        onLogout={() => { setMe(null); setPhase("anonymous"); setMysqlView("identity"); }}
      />;
    }
    if (mysqlView === "evidence" && me.trustedContext) {
      return <Workbench
        initialMe={me}
        onOpenIdentity={() => setMysqlView("identity")}
        onOpenReview={() => setMysqlView("review")}
        onOpenSecurity={() => setMysqlView("security")}
        onLogout={() => { setMe(null); setPhase("anonymous"); setMysqlView("identity"); }}
      />;
    }
    return <IdentityOnboarding
      initialMe={me}
      onProfileChange={(profile: MysqlMe) => setMe(profile)}
      onOpenEvidence={() => setMysqlView("evidence")}
      onOpenReview={() => setMysqlView("review")}
      onOpenSecurity={() => setMysqlView("security")}
      onLogout={() => { setMe(null); setPhase("anonymous"); setMysqlView("identity"); }}
    />;
  }
  return <Workbench initialMe={me} onLogout={() => { setMe(null); setPhase("anonymous"); }} />;
}
